import { useState } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { useProgress } from "@react-three/drei";
import VanityExperience from "./canvas/VanityExperience.jsx";
import VanityConfigurator from "./ui/VanityConfigurator.jsx";
import VanityInformationModal from "./ui/VanityInformationModal.jsx";
import VanityDrawingModal from "./ui/VanityDrawingModal.jsx";
import VanityInspirationModal from "./ui/VanityInspirationModal.jsx";
import DrawingMenu from "./ui/DrawingMenu.jsx";
import { hasDrawing, drawingFor } from "./data/vanityAssets.js";

const NAV_BUTTONS = ["DRAWING", "INFORMATION", "INSPIRATIONS"];

// Shows while models/textures are still downloading, so the canvas never just
// sits blank (reported as "there is a delay in displaying the 3D models").
function CanvasLoader() {
  const { active, progress } = useProgress();
  if (!active) return null;
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <span className="text-[11px] tracking-[0.25em] text-gray-400 uppercase">
        Loading {Math.round(progress)}%
      </span>
    </div>
  );
}

export default function App() {
  const [countertopPath, setCountertopPath] = useState(null);
  const [storagePath, setStoragePath] = useState(null);
  const [textures, setTextures] = useState({ countertop: null, storage: null });
  const [totalPrice, setTotalPrice] = useState(0);
  const [info, setInfo] = useState(null);

  const [showInfoModal, setShowInfoModal] = useState(false);
  const [drawingMenuOpen, setDrawingMenuOpen] = useState(false);
  const [drawing, setDrawing] = useState(null); // { src, title }
  const [showInspirationsModal, setShowInspirationsModal] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  function handleTextureChange(key, path) {
    setTextures((prev) => ({ ...prev, [key]: path }));
  }

  // Only offer parts that actually have a technical drawing.
  const drawingOptions = [];
  if (hasDrawing(info?.countertop)) drawingOptions.push({ key: "countertop", label: "Countertop" });
  if (hasDrawing(info?.storage)) drawingOptions.push({ key: "storage", label: "Storage" });

  function openDrawing(key) {
    const item = key === "storage" ? info?.storage : info?.countertop;
    setDrawing({ src: drawingFor(item), title: key === "storage" ? "Storage" : "Countertop" });
    setDrawingMenuOpen(false);
  }

  function handleNavClick(label) {
    if (!countertopPath) return;
    if (label === "INFORMATION") setShowInfoModal(true);
    if (label === "DRAWING") {
      // one drawing available -> open it directly; two -> let the user choose
      if (drawingOptions.length === 1) openDrawing(drawingOptions[0].key);
      else if (drawingOptions.length > 1) setDrawingMenuOpen((v) => !v);
    }
    if (label === "INSPIRATIONS") setShowInspirationsModal(true);
  }

  const ready = !!countertopPath;

  return (
    <div className="flex flex-col md:flex-row w-full h-full overflow-hidden">
      {/* Canvas */}
      <div
        className={`relative w-full shrink-0 bg-gray-50 transition-all duration-300 ease-in-out md:h-full ${
          panelCollapsed ? "flex-1 md:w-full" : "h-[45vh] md:w-3/5"
        }`}
      >
        <Canvas
          shadows
          gl={{
            antialias: true,
            logarithmicDepthBuffer: true,
            // Khronos PBR Neutral — keeps each finish's real colour. The
            // reference app uses the three.js default (ACES), which under this
            // rig washes "Sanded Sahara" to R-B 3 vs its swatch's 32; Neutral
            // renders it at 27. See RENDERING_README.txt.
            toneMapping: THREE.NeutralToneMapping,
            toneMappingExposure: 0.86,
          }}
          camera={{ fov: 45, near: 0.01, far: 50, position: [0, 3, 8] }}
        >
          <VanityExperience
            countertopPath={countertopPath}
            storagePath={storagePath}
            textures={textures}
          />
        </Canvas>

        <CanvasLoader />

        {/* Panel toggle — desktop only */}
        <button
          onClick={() => setPanelCollapsed((v) => !v)}
          className="absolute right-0 top-1/2 -translate-y-1/2 hidden md:flex items-center justify-center w-4 h-16 z-10 cursor-pointer"
          aria-label={panelCollapsed ? "Expand panel" : "Collapse panel"}
        >
          <div className="w-1.5 h-14 bg-gray-800 rounded-full transition-opacity opacity-60 hover:opacity-100" />
        </button>

        <div className="absolute bottom-4 md:bottom-6 left-0 right-0 flex justify-center gap-2 md:gap-3">
          {NAV_BUTTONS.map((label) => (
            <div key={label} className="relative group">
              {label === "DRAWING" && drawingMenuOpen && (
                <DrawingMenu
                  options={drawingOptions}
                  onPick={openDrawing}
                  onClose={() => setDrawingMenuOpen(false)}
                />
              )}
              <button
                onClick={() => handleNavClick(label)}
                className={`px-3 md:px-5 py-1.5 md:py-2 text-[10px] md:text-xs tracking-widest uppercase rounded-full transition-colors ${
                  ready && !(label === "DRAWING" && drawingOptions.length === 0)
                    ? "bg-gray-400 text-white hover:bg-gray-500 cursor-pointer"
                    : "border border-gray-300 text-gray-300 bg-transparent cursor-default"
                }`}
              >
                {label}
              </button>
              {!ready && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Select a countertop
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Configurator panel */}
      <div
        className={`border-gray-200 flex flex-col min-h-0 overflow-hidden transition-all duration-300 ease-in-out w-full border-t md:border-t-0 ${
          panelCollapsed ? "shrink-0 md:w-0 md:border-0" : "flex-1 md:w-2/5 md:border-l"
        }`}
      >
        {/* Panel toggle — mobile only */}
        <button
          onClick={() => setPanelCollapsed((v) => !v)}
          className="flex md:hidden items-center justify-center w-full h-4 shrink-0 cursor-pointer"
          aria-label={panelCollapsed ? "Expand panel" : "Collapse panel"}
        >
          <div className="w-14 h-1.5 bg-gray-800 rounded-full transition-opacity opacity-60 hover:opacity-100" />
        </button>

        <div
          className={`flex-1 overflow-y-auto min-w-[280px] ${panelCollapsed ? "hidden md:block" : ""}`}
        >
          <VanityConfigurator
            onCountertopModel={setCountertopPath}
            onStorageModel={setStoragePath}
            onTextureChange={handleTextureChange}
            onPriceChange={setTotalPrice}
            onInfoChange={setInfo}
          />
        </div>

        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 bg-white shrink-0 min-w-[280px]">
          <span className="text-sm text-gray-900">
            <span className="font-bold">TOTAL:</span> QAR{" "}
            {totalPrice.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <button
            className={`px-4 md:px-5 py-2 text-white text-sm tracking-wide transition-colors ${
              ready ? "bg-gray-400 hover:bg-gray-500 cursor-pointer" : "bg-gray-200 cursor-default"
            }`}
            disabled={!ready}
          >
            Add
          </button>
        </div>
      </div>

      {showInfoModal && (
        <VanityInformationModal info={info} onClose={() => setShowInfoModal(false)} />
      )}
      {drawing && (
        <VanityDrawingModal
          src={drawing.src}
          title={drawing.title}
          onClose={() => setDrawing(null)}
        />
      )}
      {showInspirationsModal && (
        <VanityInspirationModal info={info} onClose={() => setShowInspirationsModal(false)} />
      )}
    </div>
  );
}
